import i18next from "i18next";
import moment from "moment";
import React, { useContext, useEffect } from "react";
import { RequirementsProps } from "../../../tools/context";
import { useTranslation } from "../../../tools/i18n";
import { getKnowMoreLink } from "../../../tools/utils";
import { Button } from "../../Button";
import { Picto } from "../../Picto";
import { DirectionContext } from "../DirectionContext";
import NightDayHeader from "../NightDayHeader";
import NightDayHeaderDay from "../NightDayHeaderDay";
import Articles from "../ResultArticles";
import { LegInfo } from "../TravelDays";
import { TravelPlanerResultsContext } from "../TravelPlanerResultsContext";
import PriceBox from "./PriceBox";
import TravelNightPoint from "./TravelNightPoint";

const DEFAULT_IMAGE =
  "https://cdn.cibler.io/static/huttopia/DefautExperiences.png";

interface Entry {
  key: string;
  value: any[];
}

interface Activity {
  id: string;
  name?: string;
  store_name?: string;
  city?: string;
  urlimg?: string;
  url?: string;
  description?: string;
}

interface TransformedActivity {
  id: string;
  title: string;
  place: string;
  image: string;
  fallbackImage: string;
  url: string;
  description: string;
  requirements: RequirementsProps;
}

export interface TravelDaysNightProps {
  requirements: RequirementsProps;
  dayIndex?: number;
  allData?: Record<string, Entry[]>;
  resultsInOneTab?: boolean;
}

const transformActivity = (
  activity: Activity,
  requirements: RequirementsProps,
  defaultImage: string,
): TransformedActivity => ({
  id: activity.id,
  title: activity.name || activity.store_name || "",
  place: activity.city || "",
  image: activity.urlimg || "",
  fallbackImage: defaultImage,
  url: activity.url || getKnowMoreLink(requirements, activity) || "",
  description: activity.description || "",
  requirements,
});

const extractSpots = (
  data: Record<string, Entry[]> | undefined,
): Activity[] => {
  if (!data) return [];

  return Object.values(data).flatMap((dayEntries) =>
    dayEntries
      .filter((entry) => entry?.key.startsWith("night"))
      .flatMap((entry) => (Array.isArray(entry.value) ? entry.value : [])),
  );
};

const extractActivitiesForDay = (
  dayEntries: Entry[],
  requirements: RequirementsProps,
  defaultImage: string,
): TransformedActivity[] => {
  return dayEntries
    .filter((entry) => !entry?.key.startsWith("night"))
    .flatMap((entry) => (Array.isArray(entry.value) ? entry.value : []))
    .map((activity) => transformActivity(activity, requirements, defaultImage));
};

export const TravelDaysNight: React.FC<TravelDaysNightProps> = ({
  dayIndex,
  requirements,
  allData,
  resultsInOneTab,
}) => {
  const { t, language } = useTranslation();
  const {
    trip,
    inputs,
    config,
    setDay,
    setShowAllSites,
    setShowMobileMap,
    openSpotDetail,
    openSpotIframe,
  } = useContext(TravelPlanerResultsContext);
  const { currentProfile, selectedRoute } = useContext(DirectionContext);
  const allSpots = extractSpots(allData);
  const isHuttopia = requirements?.config?.key === "huttopia";

  const handleSpotClick = (spot: any) => {
    if (isHuttopia) {
      const url = getKnowMoreLink(requirements, spot);
      if (url && url !== "#") {
        openSpotIframe?.(url, spot?.name || spot?.store_name);
        return;
      }
    }
    openSpotDetail?.(spot);
  };

  useEffect(() => {
    if (!resultsInOneTab || !setDay || typeof window === "undefined") return;
    const dayKeys = allData ? Object.keys(allData) : [];
    if (!dayKeys.length) return;

    const ACTIVE_THRESHOLD = 180;
    let lastActiveKey: string | null = null;
    let frame = 0;

    const computeActive = () => {
      const suppressUntil = (window as any).__suppressActiveSpotUntil ?? 0;
      if (Date.now() < suppressUntil) return;

      let activeIndex = -1;
      for (let i = 0; i < dayKeys.length; i++) {
        const el = document.getElementById(`travelPoint_${i}`);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= ACTIVE_THRESHOLD) activeIndex = i;
      }
      if (activeIndex === -1) activeIndex = 0;

      const key = dayKeys[activeIndex];
      if (key && key !== lastActiveKey) {
        lastActiveKey = key;
        setDay(key);
      }
    };

    const onScrollOrResize = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        computeActive();
      });
    };

    computeActive();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [resultsInOneTab, allData, setDay]);
  if (dayIndex === undefined && !resultsInOneTab) {
    return null;
  }

  const dayValues = allData ? Object.values(allData) : [];

  const nightData = dayIndex ? trip[Object.keys(trip || {})[dayIndex - 1]] || null : null;
  const activities =
    dayIndex && dayIndex <= dayValues.length
      ? extractActivitiesForDay(
          dayValues[dayIndex - 1] as Entry[],
          requirements,
          DEFAULT_IMAGE,
        )
      : [];

  if (!nightData && !resultsInOneTab) return null;

  const formatStepDate = (index: number): string | undefined => {
    if (!inputs?.startDate) return undefined;
    const parsed = moment(inputs.startDate);
    if (!parsed.isValid()) return undefined;
    const rawLang = language || i18next.language || "fr";
    const momentLocale = rawLang.split(/[-_]/)[0];
    return parsed.locale(momentLocale).add(index, "day").format("dddd D MMMM");
  };

  const handleAddStep = () => {
    window.scrollTo({ top: 0 });
    setShowAllSites?.(true);
    setShowMobileMap?.(false);
  };

  const addStepButton = (
    <Button className="addStep addStepMore" onClick={handleAddStep}>
      <div className="iconWrapper">
        <Picto iconKey="plus" />
      </div>
      {t("travelPlaner.Travel.addStep")}
    </Button>
  );

  const shouldShowEnoughPoi =
    !!config?.minNumberOfPoi &&
    config.minNumberOfPoi > 0 &&
    allSpots.length < config.minNumberOfPoi;

  return (
    <div className="travelDaysNight">
      <NightDayHeader dayIndex={dayIndex} requirements={requirements} />
      {resultsInOneTab ? (
        <>
          {allSpots.map((spot, index) => {
            const dayActivities =
              index < dayValues.length
                ? extractActivitiesForDay(
                    dayValues[index] as Entry[],
                    requirements,
                    DEFAULT_IMAGE,
                  )
                : [];
            const leg = selectedRoute?.legs?.[index];
            const isLast = index === allSpots.length - 1;
            return (
              <div id={`travelPoint_${index}`} key={spot?.id || index}>
                <NightDayHeaderDay
                  dayIndex={index + 1}
                  spotName={spot?.name}
                  spot={spot}
                  date={formatStepDate(index)}
                  onSpotClick={handleSpotClick}
                />
                <TravelNightPoint
                  nightData={spot}
                  requirements={requirements}
                />
                <Articles
                  activities={dayActivities}
                  requirements={requirements}
                />
                {!isLast && currentProfile != null && leg != null ? (
                  <LegInfo
                    profile={currentProfile}
                    distanceMeters={leg.distance}
                    durationSeconds={leg.duration}
                  />
                ) : null}
              </div>
            );
          })}
          {shouldShowEnoughPoi ? (
            <em className="enoughPoi">
              {t("travelPlaner.Travel.enoughPoi")}
              {addStepButton}
            </em>
          ) : (
            addStepButton
          )}
          <div className="NightDayPriceBlocBottom">
            <PriceBox requirements={requirements} />
          </div>
        </>
      ) : (
        <>
          <div id={`travelPoint_${dayIndex - 1}`}>
            <TravelNightPoint
              key={nightData.id}
              nightData={nightData}
              requirements={requirements}
            />
            <Articles activities={activities} requirements={requirements} />
          </div>
        </>
      )}
    </div>
  );
};
